import { CustomPopupAdapter } from '../adapters/custom';
import { Popup } from '../popup';
import { PopupSniffer } from '../types';

export class CustomPopupSniffer implements PopupSniffer {
  sniff(): boolean {
    return true;
  }

  /**
   * Checks if an element has popup-like characteristics
   */
  private checkForPopup(element: HTMLElement): void {
    // Skip small elements
    if (element.offsetWidth < 100 || element.offsetHeight < 100) return;

    const style = window.getComputedStyle(element);

    // Check for popup-like CSS properties
    const isPopupLike =
      // Position fixed or absolute
      (style.position === 'fixed' || style.position === 'absolute') &&
      // Has a high z-index
      parseInt(style.zIndex, 10) > 10 &&
      // Is visible
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      // Likely to be a modal/popup
      // Either covers a significant portion of the viewport
      (element.offsetWidth > window.innerWidth * 0.5 ||
        element.offsetHeight > window.innerHeight * 0.5 ||
        // Or is centered on the screen
        style.top.includes('50%') ||
        style.left.includes('50%') ||
        style.transform.includes('translate') ||
        // Check if element is roughly in the center of the viewport
        (element.getBoundingClientRect().top > window.innerHeight * 0.1 &&
          element.getBoundingClientRect().left > window.innerWidth * 0.1 &&
          element.getBoundingClientRect().right < window.innerWidth * 0.9 &&
          element.getBoundingClientRect().bottom < window.innerHeight * 0.9));

    if (isPopupLike) {
      // Check if this element has a close button
      const hasCloseButton = this.hasCloseButton(element);

      // If it looks like a popup and has a close button, it's probably a popup
      if (hasCloseButton) {
        console.debug('Detected potential popup:', element);
        this.onDetect(element);
      }
    }
  }

  /**
   * Checks if an element has a close button
   */
  private hasCloseButton(element: HTMLElement): boolean {
    // Common close button selectors
    const closeSelectors = [
      'button.close',
      '.close',
      '.close-button',
      '.closeButton',
      '[aria-label="Close"]',
      '[aria-label="close"]',
      'button:only-child', // Often a single button is a close button
      // Common close button text
      'button:contains("Close")',
      'button:contains("×")',
      'button:contains("✕")',
    ];

    // Check for elements that look like close buttons
    const buttons = element.querySelectorAll('button, a, [role="button"]');
    for (const button of Array.from(buttons)) {
      // Small buttons in the corner are often close buttons
      const rect = button.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Check if button is in the top-right corner
      const isInTopRight = rect.right > elementRect.right - 50 && rect.top < elementRect.top + 50;

      if (isInTopRight) return true;

      // Check for X symbol or close text
      const buttonText = button.textContent?.trim();
      if (
        buttonText === '×' ||
        buttonText === '✕' ||
        buttonText === 'Close' ||
        buttonText === 'close' ||
        buttonText === 'X'
      ) {
        return true;
      }
    }

    // Check for any of the common close button selectors
    for (const selector of closeSelectors) {
      try {
        if (element.querySelector(selector)) return true;
      } catch (e) {
        // Some selectors might not be valid, ignore errors
      }
    }

    return false;
  }

  onDetect(element: HTMLElement): void {
    console.debug('CustomPopupSniffer::onDetect triggered!');
    if (!element) return;

    const selector = element.id
      ? `#${element.id}`
      : `.${element.classList.toString().replace(/\s+/g, '.')}`;

    if (!selector) return;

    if (window.popupMonitor.getPopup(selector)) return;

    const adapter = new CustomPopupAdapter({
      id: selector,
      formSelector: selector,
    });
    const rootElement = adapter.getRootElement();

    if (!rootElement) return;

    const newPopup = new Popup(adapter, {
      id: selector,
      initiallyVisible: true,
      onClose: (popup) => {
        console.debug(`Custom popup ${popup.getId()} was closed`);
      },
    });

    // Watch for style changes that might indicate the popup was closed
    const styleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const element = mutation.target as HTMLElement;
          const displayStyle = window.getComputedStyle(element).display;

          if (displayStyle === 'none' && newPopup.isVisible()) {
            // The popup was hidden via style changes
            console.debug(`Custom popup ${selector} was closed via style change`);
            window.popupMonitor.hidePopup(selector);
          }
        }
      });
    });

    styleObserver.observe(rootElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    // Also listen for the element being removed from DOM
    const parentObserver = new MutationObserver(() => {
      if (!document.body.contains(rootElement) && newPopup.isVisible()) {
        // The popup was removed from the DOM
        console.debug(`Custom popup ${selector} was removed from DOM`);
        window.popupMonitor.hidePopup(selector);
        parentObserver.disconnect();
        styleObserver.disconnect();
      }
    });

    parentObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.popupMonitor.addPopup(newPopup);
  }

  stop(): boolean {
    return false;
  }
}
